import { DataGrid, type DataGridProps, type GridValidRowModel } from "@mui/x-data-grid";
import Box from "@mui/material/Box";

/**
 * Chiều cao mặc định của bảng có phân trang: co theo màn hình nhưng luôn là số cố
 * định của khung — nhờ vậy footer phân trang ghim đúng một chỗ, không nhảy lên xuống
 * theo số dòng hay theo dòng cao thấp khác nhau. Trừ hao chiều cao header trang +
 * toolbar lọc; kẹp lại để màn nhỏ vẫn đủ dùng và màn lớn không kéo dài vô tận.
 */
const DEFAULT_HEIGHT = "clamp(420px, calc(100vh - 260px), 900px)";

/**
 * Bọc DataGrid với mặc định dùng chung. Cột nên khai báo `flex` thay vì `width`
 * để bảng tự co giãn theo khung — đó là lý do chuyển sang MUI.
 *
 * Chiều cao dòng để `auto`: nhiều ô render 2–3 dòng (tên + phụ đề), nếu giữ chiều cao
 * cố định của density compact thì nội dung bị cắt và chèn lên nhau.
 */
export function DataTable<R extends GridValidRowModel>({
  rows,
  columns,
  height,
  compactFooter,
  headerHeight,
  sx,
  ...rest
}: DataGridProps<R> & {
  /** Chiều cao khung. Bỏ trống: bảng có phân trang dùng mặc định, bảng nhỏ tự co. */
  height?: number | string;
  compactFooter?: boolean;
  /** Tăng lên khi header có 2 dòng (ví dụ cột nguồn của ma trận). */
  headerHeight?: number;
}) {
  // Bảng ẩn footer thì không có gì để ghim → để tự co theo nội dung.
  // Còn lại luôn dùng khung cố định: vùng dòng cuộn bên trong, header cột và
  // footer phân trang đứng yên thay vì trôi theo số dòng của trang.
  // `height` tường minh luôn được tôn trọng (kể cả khi ẩn footer); nếu không có
  // thì bảng ẩn footer sẽ tự co theo nội dung, còn lại dùng khung mặc định.
  const resolvedHeight = height ?? (compactFooter ? undefined : DEFAULT_HEIGHT);
  const autoHeight = resolvedHeight == null;

  return (
    <Box sx={{ width: "100%", minWidth: 0, ...(autoHeight ? {} : { height: resolvedHeight }) }}>
      <DataGrid
        rows={rows}
        columns={columns}
        density="compact"
        disableRowSelectionOnClick
        pageSizeOptions={[10, 25, 50, 100]}
        hideFooter={compactFooter}
        getRowHeight={() => "auto"}
        // Ước lượng cho phần chưa dựng — thiếu nó thì thanh cuộn nhảy khi dòng cao khác nhau.
        getEstimatedRowHeight={() => 52}
        columnHeaderHeight={headerHeight ?? 44}
        // Khi `loading` (action đang chạy) hiện thanh tiến trình mảnh phía trên,
        // KHÔNG che dòng — dữ liệu vẫn xem được trong lúc thao tác.
        slotProps={{
          loadingOverlay: {
            variant: "linear-progress",
            noRowsVariant: "linear-progress",
          },
        }}
        {...rest}
        {...(autoHeight ? { autoHeight: true } : {})}
        sx={[
          {
            // Với getRowHeight "auto", padding phải tự đặt — mặc định của MUI bằng 0.
            "& .MuiDataGrid-cell": {
              py: 1,
              px: 1.25,
              minHeight: 44,
              display: "flex",
              alignItems: "center",
              lineHeight: 1.45,
            },
            "& .MuiDataGrid-columnHeader": { px: 1.25 },
            // Cột checkbox không có nội dung text nên bỏ padding ngang chung ở
            // trên và căn giữa — nếu không, ô checkbox ở header và ở dòng lệch
            // nhau một khoảng padding.
            "& .MuiDataGrid-cellCheckbox, & .MuiDataGrid-columnHeaderCheckbox": {
              px: 0,
              justifyContent: "center",
            },
            "& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer": {
              justifyContent: "center",
              padding: 0,
            },
            // Ô một dòng vẫn cắt ellipsis thay vì phá vỡ chiều rộng cột.
            "& .MuiDataGrid-cell > *": { minWidth: 0 },
            "& .row-disabled": { opacity: 0.55 },
            // Header và footer đục — dòng cuộn phía dưới không lộ qua.
            "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-footerContainer": {
              backgroundColor: "background.paper",
            },
          },
          // sx có thể là object hoặc mảng — dạng mảng gộp được mà không ghi đè.
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      />
    </Box>
  );
}
